import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { CloudflareVideoPlayer } from "@/components/cloudflare-video-player";
import { FlipHorizontal, Volume2, VolumeX, Film, Loader2 } from "lucide-react";
import type { VideoDescriptor } from "@/components/video-picker-drawer";

export interface YTPlayer {
  playVideo: () => void;
  pauseVideo: () => void;
  seekTo: (seconds: number, allowSeekAhead: boolean) => void;
  getCurrentTime: () => number;
  getDuration: () => number;
  getPlayerState: () => number;
  setPlaybackRate: (rate: number) => void;
  destroy: () => void;
  mute: () => void;
  unMute: () => void;
  isMuted: () => boolean;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const YTApi = () => (window as any).YT as {
  Player: new (elementId: string, config: {
    videoId: string;
    width?: string | number;
    height?: string | number;
    playerVars?: Record<string, number | string>;
    events?: {
      onReady?: (event: { target: YTPlayer }) => void;
      onStateChange?: (event: { data: number; target: YTPlayer }) => void;
    };
  }) => YTPlayer;
  PlayerState: { PLAYING: number; PAUSED: number; ENDED: number };
};

export function loadYouTubeAPI(): Promise<void> {
  return new Promise((resolve) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const w = window as any;
    if (w.YT && w.YT.Player) { resolve(); return; }
    const existingScript = document.getElementById("youtube-iframe-api");
    if (existingScript) {
      const prev = w.onYouTubeIframeAPIReady;
      w.onYouTubeIframeAPIReady = () => { prev?.(); resolve(); };
      return;
    }
    const script = document.createElement("script");
    script.id = "youtube-iframe-api";
    script.src = "https://www.youtube.com/iframe_api";
    w.onYouTubeIframeAPIReady = () => resolve();
    document.body.appendChild(script);
  });
}

interface CompareVideoSlotProps {
  fillHeight?: boolean;
  slotId: "A" | "B";
  label: string;
  descriptor: VideoDescriptor;
  isMirrored: boolean;
  isAudioActive: boolean;
  playbackSpeed: number;
  onVideoRef: (el: HTMLVideoElement | null) => void;
  onYtPlayerReady: (player: YTPlayer) => void;
  onDurationChange: (d: number) => void;
  onTimeUpdate: (t: number) => void;
  onPlay: () => void;
  onPause: () => void;
  onPickVideo: () => void;
  onToggleMirror: () => void;
  onToggleAudio: () => void;
}

export function CompareVideoSlot({
  fillHeight = false,
  slotId,
  label,
  descriptor,
  isMirrored,
  isAudioActive,
  playbackSpeed,
  onVideoRef,
  onYtPlayerReady,
  onDurationChange,
  onTimeUpdate,
  onPlay,
  onPause,
  onPickVideo,
  onToggleMirror,
  onToggleAudio,
}: CompareVideoSlotProps) {
  const ytContainerRef = useRef<HTMLDivElement>(null);
  const ytPlayerRef = useRef<YTPlayer | null>(null);
  const timeIntervalRef = useRef<number | null>(null);
  const [cfProcessing, setCfProcessing] = useState(false);
  const [cfHlsUrl, setCfHlsUrl] = useState<string | null>(null);

  // Cloudflare processing poll
  useEffect(() => {
    if (descriptor?.type !== "cloudflare") {
      setCfProcessing(false);
      setCfHlsUrl(null);
      return;
    }
    const { streamId, hlsUrl } = descriptor;
    if (hlsUrl) {
      setCfHlsUrl(hlsUrl);
      setCfProcessing(false);
      return;
    }

    setCfProcessing(true);
    let mounted = true;
    let pollTimer: ReturnType<typeof setTimeout> | null = null;
    let failCount = 0;

    const checkStatus = async () => {
      try {
        const res = await fetch(`/api/cloudflare/status/${streamId}`, { credentials: "same-origin" });
        if (!mounted) return;
        if (!res.ok) {
          failCount++;
          if (failCount >= 3) { setCfProcessing(false); return; }
          pollTimer = setTimeout(checkStatus, 3000);
          return;
        }
        const data = await res.json();
        if (data.readyToStream && data.playback?.hls) {
          setCfProcessing(false);
          setCfHlsUrl(data.playback.hls);
        } else {
          pollTimer = setTimeout(checkStatus, 3000);
        }
      } catch {
        failCount++;
        if (mounted && failCount < 3) pollTimer = setTimeout(checkStatus, 5000);
        else if (mounted) setCfProcessing(false);
      }
    };

    checkStatus();
    return () => { mounted = false; if (pollTimer) clearTimeout(pollTimer); };
  }, [descriptor]);

  // YouTube player init
  useEffect(() => {
    if (descriptor?.type !== "youtube") {
      if (ytPlayerRef.current) { ytPlayerRef.current.destroy(); ytPlayerRef.current = null; }
      if (timeIntervalRef.current) { clearInterval(timeIntervalRef.current); timeIntervalRef.current = null; }
      return;
    }

    const { youtubeId } = descriptor;
    let mounted = true;
    const containerId = `yt-player-${slotId}`;

    const initPlayer = async () => {
      await loadYouTubeAPI();
      if (!mounted || !ytContainerRef.current) return;

      if (ytPlayerRef.current) { ytPlayerRef.current.destroy(); ytPlayerRef.current = null; }

      ytContainerRef.current.innerHTML = `<div id="${containerId}" style="width:100%;height:100%;"></div>`;

      ytPlayerRef.current = new (YTApi().Player)(containerId, {
        videoId: youtubeId,
        width: "100%",
        height: "100%",
        playerVars: {
          modestbranding: 1,
          rel: 0,
          controls: 0,
          disablekb: 1,
          fs: 0,
          iv_load_policy: 3,
          playsinline: 1,
        },
        events: {
          onReady: (event) => {
            if (!mounted) return;
            onYtPlayerReady(event.target);
            onDurationChange(event.target.getDuration());
            if (playbackSpeed !== 1) event.target.setPlaybackRate(playbackSpeed);
            if (!isAudioActive) event.target.mute();
          },
          onStateChange: (event) => {
            if (!mounted) return;
            const yt = YTApi();
            if (event.data === yt.PlayerState.PLAYING) {
              onPlay();
              onDurationChange(event.target.getDuration());
            } else if (event.data === yt.PlayerState.PAUSED) {
              onPause();
            }
          },
        },
      });

      timeIntervalRef.current = window.setInterval(() => {
        if (ytPlayerRef.current && mounted) {
          try {
            const t = ytPlayerRef.current.getCurrentTime();
            if (typeof t === "number") onTimeUpdate(t);
          } catch {}
        }
      }, 250);
    };

    initPlayer();

    return () => {
      mounted = false;
      if (timeIntervalRef.current) { clearInterval(timeIntervalRef.current); timeIntervalRef.current = null; }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [descriptor, slotId]);

  // Sync mute state for YouTube
  useEffect(() => {
    if (!ytPlayerRef.current) return;
    try {
      if (isAudioActive) ytPlayerRef.current.unMute();
      else ytPlayerRef.current.mute();
    } catch {}
  }, [isAudioActive]);

  const isEmpty = !descriptor;

  return (
    <div className={`flex flex-col min-w-0 ${fillHeight ? "h-full" : ""}`}>
      {/* Slot header */}
      <div className="flex items-center gap-1 px-2 py-1.5 bg-card border-b shrink-0">
        <span className="w-6 h-6 rounded bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center shrink-0">
          {slotId}
        </span>
        <span className="text-sm font-medium truncate flex-1">{label}</span>
        <Button
          variant="ghost"
          size="icon"
          className="w-7 h-7 shrink-0"
          onClick={onToggleMirror}
          title={isMirrored ? "Mirrored" : "Normal"}
        >
          <FlipHorizontal className={`w-4 h-4 ${isMirrored ? "text-primary" : "text-muted-foreground"}`} />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="w-7 h-7 shrink-0"
          onClick={onToggleAudio}
          title={isAudioActive ? "Mute" : "Unmute"}
        >
          {isAudioActive
            ? <Volume2 className="w-4 h-4 text-primary" />
            : <VolumeX className="w-4 h-4 text-muted-foreground" />}
        </Button>
        <Button variant="outline" size="sm" className="text-xs h-7 px-2 shrink-0" onClick={onPickVideo}>
          {isEmpty ? "Pick video" : "Change"}
        </Button>
      </div>

      {/* Video area */}
      <div className={`relative bg-black overflow-hidden ${fillHeight ? "flex-1" : "aspect-video w-full"}`}>
        {isEmpty ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
            <Film className="w-10 h-10 text-muted-foreground opacity-40" />
            <Button variant="outline" onClick={onPickVideo}>Pick a video</Button>
          </div>
        ) : descriptor.type === "cloudflare" ? (
          <>
            {cfProcessing && (
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-black/80 gap-3">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
                <p className="text-white text-sm">Processing video…</p>
              </div>
            )}
            {cfHlsUrl && (
              <div
                className="w-full h-full"
                style={{ transform: isMirrored ? "scaleX(-1)" : "none" }}
              >
                <CloudflareVideoPlayer
                  hlsUrl={cfHlsUrl}
                  playbackSpeed={playbackSpeed}
                  muted={!isAudioActive}
                  onVideoRef={onVideoRef}
                  onTimeUpdate={onTimeUpdate}
                  onDurationChange={onDurationChange}
                  onPlay={onPlay}
                  onPause={onPause}
                  className="w-full h-full object-contain pointer-events-none"
                />
              </div>
            )}
          </>
        ) : descriptor.type === "youtube" ? (
          <div
            ref={ytContainerRef}
            className="w-full h-full [&>div]:w-full [&>div]:h-full [&>iframe]:w-full [&>iframe]:h-full pointer-events-none"
            style={{ transform: isMirrored ? "scaleX(-1)" : "none" }}
          />
        ) : null}
      </div>
    </div>
  );
}
